/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Hidden, breakpoints } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import type { AdminType } from '@coursera/event-pulse-types';
import { track } from '@coursera/event-pulse/sdk';

import GlobalHelpButton from 'bundles/common/components/GlobalHelpButton';
import { PrivateAuthoringPartnerContext } from 'bundles/enterprise-admin-custom-courses/components/PrivateAuthoringPartnerProvider';
import { noPrivateAuthoringCanUseCustomCourses } from 'bundles/enterprise-admin-custom-courses/utils/helpers';
import { getPrivateAuthoringPartnerIdForCourseBuilder } from 'bundles/enterprise-admin-custom-courses/utils/privateAuthoringUtils';
import { ADMIN_HOME_V2_REF } from 'bundles/enterprise-admin-home/constants';
import AdminNavigationDropdown from 'bundles/enterprise-admin-navigation/components/AdminNavigationDropdown';
import CourseraHeader from 'bundles/enterprise-admin-navigation/components/CourseraHeader';
import MenuItem from 'bundles/enterprise-admin-navigation/components/MenuItem';
import NavMenu from 'bundles/enterprise-admin-navigation/components/NavMenu';
import { ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX } from 'bundles/enterprise-admin-navigation/constants';
import { getMenuLinks } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import withManagedOrganizations from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { EnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/page';

type PropsFromCaller = {
  thirdPartyOrganization: AdminDashboardOrg;
  adminRoleType?: string;
  isAdminCoachV2Enabled: boolean;
};

type PropsFromOrganizations = {
  thirdPartyOrganizations: AdminDashboardOrg[];
};

type Props = PropsFromCaller & PropsFromOrganizations;

const styles = {
  askCourseraMenuItem: css`
    display: flex;
    align-items: center;
    height: 100%;
    ${breakpoints.down('xs')} {
      border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
};

export class AdminHeaderAndNav extends React.Component<Props> {
  private prevPathName?: string;

  static contextTypes = {
    router: PropTypes.object.isRequired,
    enableSkillsPlanningForOrg: PropTypes.bool.isRequired,
    enableNewLearnersPage: PropTypes.bool.isRequired,
    enableLearnerGroups: PropTypes.bool.isRequired,
    enableMessagingV2: PropTypes.bool,
  };

  declare context: LegacyContextType<typeof AdminHeaderAndNav.contextTypes>;

  componentDidMount() {
    const { router } = this.context;
    this.trackPageView();
    this.prevPathName = router.location.pathname;
  }

  componentDidUpdate() {
    const { router } = this.context;
    // Track on route changes
    if (router.location.pathname !== this.prevPathName) {
      this.trackPageView();
      this.prevPathName = router.location.pathname;
    }
  }

  /* Tracking page view through 'view_admin_dashboard_page' event as the default 'view_page' event tracks unauthorized views as well.
    Additionally, adminType property is required for the event. */
  trackPageView() {
    const { thirdPartyOrganization, adminRoleType } = this.props;
    track('view_admin_dashboard_page', {
      enterprise: {
        organizationSlug: thirdPartyOrganization.slug,
      },
      navigationBarType: 'top_nav',
      enterpriseAdminAccount: {
        adminType: (adminRoleType ? adminRoleType.toLowerCase().replace(/ /g, '_') : undefined) as AdminType,
      },
    });
  }

  renderMenuLinks(canUseCourseBuilder: boolean) {
    const { thirdPartyOrganization } = this.props;
    const {
      router,
      enableSkillsPlanningForOrg,
      enableNewLearnersPage,
      enableLearnerGroups = false,
      enableMessagingV2 = false,
    } = this.context;

    const locationSearch = new URLSearchParams(router.location.search || '');
    if (locationSearch.get('ref') === ADMIN_HOME_V2_REF) {
      locationSearch.delete('ref');
    }
    const locationSearchString = locationSearch.size > 0 ? `?${locationSearch.toString()}` : '';

    // enableLearnerGroups is consumed by getMenuLinks to swap the Programs nav
    // link for a Groups link when the flag is enabled.
    return getMenuLinks(
      router.params,
      enableSkillsPlanningForOrg,
      canUseCourseBuilder,
      thirdPartyOrganization.id,
      enableNewLearnersPage,
      enableLearnerGroups,
      enableMessagingV2
    ).map((item) => {
      const { id, href, text, requiredPermissions, phaseOneOverride } = item;

      /* Fix for Programs tab: don't show as active when on contracts route
        since contracts is a child route of programs 
      */
      const isActive =
        id === 'PROGRAMS'
          ? router.isActive(href) && !router.location.pathname.includes('/contracts')
          : router.isActive(href);

      return requiredPermissions ? (
        <EnterprisePermissionsRequired
          domain={requiredPermissions.domain}
          domainId={requiredPermissions.domainId}
          subdomain={requiredPermissions.subdomain}
          verb={requiredPermissions.verb}
          phaseOneOverride={phaseOneOverride}
          key={id}
        >
          <MenuItem
            isActive={isActive}
            text={text}
            tag="link"
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            htmlAttributes={{
              href: `${href}${locationSearchString}`,
              'data-e2e': `Menu_Link~${text}`,
            }}
          />
        </EnterprisePermissionsRequired>
      ) : (
        <MenuItem
          key={id}
          isActive={isActive}
          text={text}
          tag="link"
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          htmlAttributes={{
            href: `${href}${locationSearchString}`,
            'data-e2e': `Menu_Link~${text}`,
          }}
        />
      );
    });
  }

  renderCoachLauncher = () => {
    const { isAdminCoachV2Enabled, thirdPartyOrganization } = this.props;

    if (!isAdminCoachV2Enabled) {
      return null;
    }

    const askCourseraHref = `/o/${thirdPartyOrganization.slug}/admin/ask-coursera`;
    const isActive = this.context.router.isActive(askCourseraHref);
    return (
      <div css={styles.askCourseraMenuItem}>
        <MenuItem
          text=""
          tag="link"
          isActive={isActive}
          isNewFeature
          icon={<AIGenerateBrandedNewIcon size="large" aria-hidden="true" />}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          htmlAttributes={{
            href: askCourseraHref,
            'data-e2e': 'Menu_Link~AI_Assistant',
            'aria-label': _t('Conversational analytics'),
          }}
        />
      </div>
    );
  };

  renderLeftChildren = (canUseCourseBuilder: boolean) => {
    return (
      <NavMenu>
        {this.renderMenuLinks(canUseCourseBuilder)}
        {this.renderCoachLauncher()}
      </NavMenu>
    );
  };

  render() {
    const { thirdPartyOrganization, thirdPartyOrganizations } = this.props;

    const rightChildren = (
      <AdminNavigationDropdown
        isDirectSale={enterpriseAdminUser.isStandardContractUser()}
        isSelfServe={!enterpriseAdminUser.isStandardContractUser()}
        thirdPartyOrganization={thirdPartyOrganization}
        thirdPartyOrganizations={thirdPartyOrganizations}
        renderCoachLauncher={this.renderCoachLauncher}
      />
    );

    return (
      <div data-testid="AdminHeaderAndNav">
        <div data-testid="mobile">
          <Hidden mdUp>
            <CourseraHeader
              navHeight={ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX}
              leftChildren={null}
              rightChildren={rightChildren}
            />
          </Hidden>
        </div>
        <div data-testid="desktop">
          <Hidden smDown>
            <PrivateAuthoringPartnerContext.Consumer>
              {({ enterpriseAuthoringPartners }) => (
                <EnterpriseAdminContext.Consumer>
                  {({ contracts }) => (
                    <EnterpriseAdminPermissionsContext.Consumer>
                      {({ getIsOrganizationDomainAdmin }) => {
                        const canUseCourseBuilder =
                          noPrivateAuthoringCanUseCustomCourses(getIsOrganizationDomainAdmin(), contracts) ||
                          !!getPrivateAuthoringPartnerIdForCourseBuilder(enterpriseAuthoringPartners);

                        return (
                          <CourseraHeader
                            navHeight={ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX}
                            leftChildren={this.renderLeftChildren(canUseCourseBuilder)}
                            rightChildren={rightChildren}
                          />
                        );
                      }}
                    </EnterpriseAdminPermissionsContext.Consumer>
                  )}
                </EnterpriseAdminContext.Consumer>
              )}
            </PrivateAuthoringPartnerContext.Consumer>
          </Hidden>
        </div>
        <GlobalHelpButton />
      </div>
    );
  }
}

export default withManagedOrganizations<Props, PropsFromCaller>(AdminHeaderAndNav);
